import { FC, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { useAuth } from '@global-contexts/auth';

const formatRemaining = (expiresAt: number) => {
  const seconds = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * Always-visible reminder that the screen belongs to someone else.
 *
 * Deliberately loud and impossible to dismiss: mistaking an impersonated session for
 * your own is the main risk of the feature.
 */
export const IMPERSONATION_BANNER_HEIGHT = '36px';

const ImpersonationBanner: FC<{ left?: string }> = ({ left = '0px' }) => {
  const { impersonation, stopImpersonation } = useAuth();
  const [remaining, setRemaining] = useState(() => (impersonation ? formatRemaining(impersonation.expiresAt) : ''));
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!impersonation) {
      return;
    }
    const tick = () => setRemaining(formatRemaining(impersonation.expiresAt));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [impersonation]);

  if (!impersonation) {
    return null;
  }

  const { target, actorEmail } = impersonation;

  return (
    <Box
      data-testid="impersonation-banner"
      sx={{
        // Inset to the content column so the sidebar logo stays visible, matching the
        // AppBar that sits directly below it.
        position: 'fixed',
        top: 0,
        left,
        right: 0,
        height: IMPERSONATION_BANNER_HEIGHT,
        transition: 'left 0.2s ease-out',
        zIndex: 2001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        padding: '0 16px',
        overflow: 'hidden',
        backgroundColor: '#b54708',
        color: '#fff'
      }}>
      <VisibilityIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Viewing as {target.full_name || target.email}
        {target.company_name ? ` (${target.company_name})` : ''} — read only
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.85 }}>
        signed in as {actorEmail} · ends in {remaining}
      </Typography>
      <Button
        size="small"
        variant="contained"
        color="inherit"
        disabled={isExiting}
        onClick={() => {
          setIsExiting(true);
          stopImpersonation().catch(() => setIsExiting(false));
        }}
        sx={{ color: '#b54708', backgroundColor: '#fff', '&:hover': { backgroundColor: '#f3f3f3' } }}>
        {isExiting ? 'Exiting…' : 'Exit'}
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
